import { apiClient } from '../utils/apiClient';
import {
  IntroduceEmailCheck,
  IntroducedInvestorDetail,
  IntroduceInvestorLookups,
  IntroduceInvestorRequest,
  IntroducedInvestorSummary,
} from '../models/ServiceAgentIntroductionDto';

export type {
  IntroduceEmailCheck,
  IntroducedInvestorDetail,
  IntroduceInvestorLookups,
  IntroduceInvestorRequest,
  IntroducedInvestorSummary,
};

/**
 * Introduce Investor, for a Client Executive Agent.
 *
 * The service provider and the executive agent are never sent from here — the API resolves
 * both from the signed-in agent's profile, so there is nothing to pass and nothing to spoof.
 */
class ServiceAgentIntroductionService {
  private readonly base = '/api/service-agents/me/introductions';

  /** Option lists for the form, already scoped to the agent's service provider. */
  async getLookups(firmId?: string | null): Promise<IntroduceInvestorLookups> {
    const res = await apiClient.get<IntroduceInvestorLookups>(`${this.base}/lookups`, {
      params: firmId ? { firmId } : {},
    });
    return res.data;
  }

  /** Creates the investor in Power Apps. Rejects without creating anything on failure. */
  async introduce(payload: IntroduceInvestorRequest): Promise<IntroducedInvestorSummary> {
    const res = await apiClient.post<IntroducedInvestorSummary>(this.base, payload);
    return res.data;
  }

  /**
   * Whether this email already has an account or an earlier introduction from this firm.
   * Advisory only — a duplicate is a legitimate way to offer an existing investor a new product.
   */
  async checkEmail(email: string, productGuid?: string, firmId?: string | null): Promise<IntroduceEmailCheck> {
    const params: Record<string, string> = { email };
    if (productGuid) params.productGuid = productGuid;
    if (firmId) params.firmId = firmId;
    const res = await apiClient.get<IntroduceEmailCheck>(`${this.base}/email-check`, { params });
    return res.data;
  }

  async getMyIntroductions(): Promise<IntroducedInvestorSummary[]> {
    const res = await apiClient.get<IntroducedInvestorSummary[]>(this.base);
    return res.data;
  }

  /** View details — the record as Dataverse holds it now. */
  async getDetail(id: number): Promise<IntroducedInvestorDetail> {
    const res = await apiClient.get<IntroducedInvestorDetail>(`${this.base}/${id}`);
    return res.data;
  }

  /** Withdraws a pending introduction. The server refuses once the investor has registered. */
  async deactivate(id: number): Promise<IntroducedInvestorDetail> {
    const res = await apiClient.post<IntroducedInvestorDetail>(`${this.base}/${id}/deactivate`);
    return res.data;
  }
}

export const serviceAgentIntroductionService = new ServiceAgentIntroductionService();
export default serviceAgentIntroductionService;
